import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { ClientConfigCard } from './ClientConfigCard';
import { GateConfigCard } from './GateConfigCard';
import { KaycooConfigCard } from './KaycooConfigCard';
import { KaycooKeyCard } from './KaycooKeyCard';
import { AgentConfigCard } from './AgentConfigCard';
import { DangerZoneCard } from './DangerZoneCard';
import { EscalationRoutesCard } from './EscalationRoutesCard';
import { FEATURE_ESCALATIONS } from '@/lib/features';

interface ClientRecord {
  id: string;
  name: string;
  slug: string;
  status: 'active' | 'inactive' | 'suspended';
  gate_config: {
    require_all_signatures: boolean;
    require_compliance_pass: boolean;
    human_review_on_reject: boolean;
  } | null;
  settings: {
    retry_count: number;
    document_agent_version: string | null;
    clause_agent_version: string | null;
    compliance_agent_version: string | null;
  } | null;
}

/**
 * Settings page — PAGE-010. Admin-only (guarded at route level by AdminRoute).
 * No auto-approve threshold (FR-077 v3.2).
 */
export function SettingsPage() {
  const { clientId } = useAuth();
  const [client, setClient] = useState<ClientRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadClient = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    const { data, error: qErr } = await supabase
      .from('clients')
      .select('id, name, slug, status, gate_config, settings')
      .eq('id', clientId)
      .single();
    setLoading(false);
    if (qErr) {
      setError('Failed to load settings. Please try again.');
      toast.error('Failed to load settings.');
      return;
    }
    setClient(data as ClientRecord);
  }, [clientId]);

  useEffect(() => { void loadClient(); }, [loadClient]);

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-foreground">Settings</h1>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => void loadClient()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-foreground">Settings</h1>

      <ClientConfigCard
        client={client}
        isLoading={loading}
        onNameUpdated={(name) => { setClient((prev) => prev ? { ...prev, name } : prev); }}
      />

      <GateConfigCard
        clientId={clientId ?? ''}
        config={client?.gate_config ?? null}
        isLoading={loading}
        onConfigUpdated={(cfg) => { setClient((prev) => prev ? { ...prev, gate_config: cfg } : prev); }}
      />

      <KaycooKeyCard
        clientId={clientId ?? null}
        settings={client?.settings as { kaycoo_api_key?: string } & Record<string, unknown> | null ?? null}
        isLoading={loading}
        onSettingsUpdated={(s) => { setClient((prev) => prev ? { ...prev, settings: s as ClientRecord['settings'] } : prev); }}
      />

      <AgentConfigCard
        clientId={clientId ?? null}
        settings={client?.settings as { agent_assignments?: Record<string, string> } & Record<string, unknown> | null ?? null}
        isLoading={loading}
        onSettingsUpdated={(s) => { setClient((prev) => prev ? { ...prev, settings: s as ClientRecord['settings'] } : prev); }}
      />

      <KaycooConfigCard isLoading={loading} />

      {FEATURE_ESCALATIONS && <EscalationRoutesCard />}

      <DangerZoneCard clientName={client?.name ?? null} />
    </div>
  );
}
